import { useState, useEffect, useRef, useCallback } from "react";

export interface TaskEvent {
  type: "task_created" | "task_progress" | "task_stopped" | "connected" | "heartbeat";
  taskId?: string;
  message?: string;
  progressType?: string;
  stopReason?: string;
  status?: string;
  attachments?: Array<{
    fileName: string;
    url: string;
    sizeBytes: number;
  }>;
  timestamp: number;
}

interface UseTaskSSEOptions {
  taskId: string | null;
  enabled?: boolean;
}

function getApiBaseUrl(): string {
  const envUrl = (import.meta as any).env?.VITE_API_URL;
  if (envUrl) return envUrl;
  return ""; // same origin in dev
}

/**
 * Hook que conecta ao SSE endpoint para receber eventos de uma task em tempo real.
 * Mantém um array de eventos recebidos e o status de conexão.
 */
export function useTaskSSE({ taskId, enabled = true }: UseTaskSSEOptions) {
  const [events, setEvents] = useState<TaskEvent[]>([]);
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const eventSourceRef = useRef<EventSource | null>(null);

  const disconnect = useCallback(() => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
    setIsConnected(false);
  }, []);

  const clearEvents = useCallback(() => {
    setEvents([]);
  }, []);

  useEffect(() => {
    if (!taskId || !enabled) {
      disconnect();
      return;
    }

    const token = localStorage.getItem("accessToken");
    if (!token) {
      setError("Autenticação necessária");
      return;
    }

    const baseUrl = getApiBaseUrl();
    const url = `${baseUrl}/sse/task/${taskId}?token=${encodeURIComponent(token)}`;

    const es = new EventSource(url);
    eventSourceRef.current = es;

    es.addEventListener("connected", (e) => {
      setIsConnected(true);
      setError(null);
      const data = JSON.parse(e.data);
      setEvents((prev) => [...prev, { type: "connected", timestamp: data.timestamp }]);
    });

    es.addEventListener("task_created", (e) => {
      const data = JSON.parse(e.data);
      setEvents((prev) => [...prev, {
        type: "task_created",
        taskId: data.taskId,
        message: data.message,
        status: data.status,
        timestamp: data.timestamp,
      }]);
    });

    es.addEventListener("task_progress", (e) => {
      const data = JSON.parse(e.data);
      setEvents((prev) => [...prev, {
        type: "task_progress",
        taskId: data.taskId,
        message: data.message,
        progressType: data.progressType,
        status: data.status,
        timestamp: data.timestamp,
      }]);
    });

    es.addEventListener("task_stopped", (e) => {
      const data = JSON.parse(e.data);
      setEvents((prev) => [...prev, {
        type: "task_stopped",
        taskId: data.taskId,
        message: data.message,
        stopReason: data.stopReason,
        status: data.status,
        attachments: data.attachments,
        timestamp: data.timestamp,
      }]);
    });

    es.addEventListener("task_status_changed", (e) => {
      const data = JSON.parse(e.data);
      setEvents((prev) => [...prev, {
        type: "task_progress",
        taskId: data.taskId,
        message: `Status atualizado: ${data.status}`,
        status: data.status,
        timestamp: data.timestamp,
      }]);
    });

    es.addEventListener("heartbeat", () => {
      // Heartbeat silencioso — mantém conexão viva
    });

    es.onerror = () => {
      setIsConnected(false);
      setError("Conexão perdida. Reconectando...");
      // EventSource reconecta automaticamente
    };

    return () => {
      es.close();
      eventSourceRef.current = null;
      setIsConnected(false);
    };
  }, [taskId, enabled, disconnect]);

  return {
    events,
    isConnected,
    error,
    disconnect,
    clearEvents,
  };
}
